import { useState, useCallback, useEffect, useRef } from 'react'
import { isAuthError } from 'workers-common'
import { MYAGENT_PARTICIPANT_ID } from '../components/participantColors.js'

export const useChatWithSteps = (options = {}) => {
  const {
    api,
    onAuthError,
    peerService,
    onAsyncTask,
    onParticipantsChanged,
    maxIterations = 10,
    conversationId = null,
    conversationTarget = null,
    botConfig = null,
  } = options

  const [messages, setMessages] = useState([])
  const [isThinking, setIsThinking] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(true)
  const [currentStep, setCurrentStep] = useState(null)

  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId
  const pendingParticipantMsgs = useRef([])
  const pendingPersist = useRef(Promise.resolve())

  useEffect(() => {
    if (!api) return
    setIsLoadingHistory(true)
    setMessages([])
    ;(async () => {
      try {
        if (conversationId) {
          const msgs = await api.getConversationMessages(conversationId, 200)
          if (msgs?.length > 0) setMessages(msgs)
        } else {
          const response = await api.getContext({ maxMessages: 50, conversationsOnly: true })
          if (response.messages?.length > 0) {
            setMessages(response.messages
              .filter(msg => !(msg.role === 'assistant' && !msg.content))
              .map(msg => ({
                role: msg.role,
                content: msg.content,
                timestamp: msg.timestamp || Date.now(),
                ...(msg.role === 'participant' ? { participantId: msg.participantId, metadata: msg.metadata } : {}),
              })))
          }
        }
      } catch (error) {
        console.error('[useChatWithSteps] Failed to load history:', error)
      } finally {
        setIsLoadingHistory(false)
      }
    })()
  }, [api, conversationId])

  const handleToolCall = useCallback((tc) => {
    if (tc.source !== 'role') return
    const toolName = tc.toolPath?.split('/').pop() || 'task'
    const msgs = [
      { role: 'participant', participantId: MYAGENT_PARTICIPANT_ID, content: tc.toolPath || 'task',
        timestamp: Date.now(), metadata: { type: 'role_request', targetId: tc.roleId } },
      { role: 'participant', participantId: tc.roleId,
        content: tc.success ? `\u2713 ${toolName}` : `\u2717 ${toolName}: ${tc.error}`,
        timestamp: Date.now(), metadata: { type: 'tool_call', toolPath: tc.toolPath, toolArgs: tc.toolArgs, result: tc.result, success: tc.success } },
    ]
    pendingParticipantMsgs.current.push(...msgs)
    setMessages(prev => [...prev, ...msgs])
  }, [])

  const sendMessage = useCallback(
    async (content) => {
      if (!content.trim() || isThinking || !peerService) return
      const trimmed = content.trim()
      const convId = conversationIdRef.current

      const userMsg = { role: 'user', content: trimmed, timestamp: Date.now() }
      setMessages(prev => [...prev, userMsg])
      setIsThinking(true)
      pendingParticipantMsgs.current = []

      try {
        await pendingPersist.current

        const chatMessages = await api.getChatMessages(convId)

        const result = await peerService.sendMessage(trimmed, {
          target: conversationTarget,
          botConfig,
          messages: chatMessages,
          onStep: setCurrentStep,
          onToolCall: handleToolCall,
          onAsyncTask: info => onAsyncTask?.({ ...info, conversationId: convId }),
          maxIterations,
        })

        if (result.participants) onParticipantsChanged?.(result.participants)

        // Display result
        const responderId = result.responderId || MYAGENT_PARTICIPANT_ID
        let responseMsg = null

        if (result.type === 'async') {
          onAsyncTask?.({ taskId: result.taskId, roleId: result.responderId, displayName: result.responderName, conversationId: convId })
          responseMsg = {
            role: 'participant', participantId: responderId,
            content: 'Processing...', timestamp: Date.now(),
            metadata: { type: 'chat_pending', taskId: result.taskId },
          }
        } else if (result.answer) {
          responseMsg = {
            role: 'participant', participantId: responderId,
            content: result.answer, timestamp: Date.now(),
            metadata: {
              type: responderId === MYAGENT_PARTICIPANT_ID ? 'assistant_response' : 'chat_response',
              displayName: result.responderName, usage: result.usage, modelInfo: result.modelInfo, durationMs: result.durationMs,
            },
          }
        }

        if (responseMsg && (!convId || conversationIdRef.current === convId)) {
          setMessages(prev => [...prev, responseMsg])
        }

        // Unblock UI before persistence
        const savedParticipantMsgs = [...pendingParticipantMsgs.current]
        pendingParticipantMsgs.current = []
        setIsThinking(false)
        setCurrentStep(null)

        // Persist (non-blocking, tracked so next send awaits completion)
        pendingPersist.current = api.saveChatResult({
          conversationId: convId,
          userMessage: trimmed,
          responseMessage: responseMsg,
          toolInteractions: result.toolInteractions,
          participantMessages: savedParticipantMsgs,
        }).catch(err => {
          console.error('[useChatWithSteps] Persist failed:', err)
          setMessages(prev => [...prev, {
            role: 'participant', participantId: MYAGENT_PARTICIPANT_ID,
            content: 'Warning: Failed to save conversation history. Your messages may be lost on refresh.',
            timestamp: Date.now(), metadata: { type: 'system_warning' },
          }])
        })

      } catch (error) {
        console.error('[useChatWithSteps] Failed:', error)
        if (isAuthError(error)) onAuthError?.(error)
        setMessages(prev => [...prev, {
          role: 'participant', participantId: MYAGENT_PARTICIPANT_ID,
          content: `Error: ${error.message || 'Failed to send message'}`,
          timestamp: Date.now(), isError: true,
          metadata: { type: 'assistant_response' },
        }])
        pendingParticipantMsgs.current = []
        setIsThinking(false)
        setCurrentStep(null)
      }
    },
    [api, isThinking, peerService, conversationTarget, botConfig, maxIterations, onAuthError, onAsyncTask, onParticipantsChanged, handleToolCall],
  )

  const appendMessage = useCallback((msg) => {
    setMessages((prev) => {
      const taskId = msg.metadata?.taskId
      if (!taskId) return [...prev, msg]
      const idx = prev.findIndex(m => m.metadata?.taskId === taskId &&
        (m.metadata?.type === 'progress' || m.metadata?.type === 'chat_pending'))
      if (idx >= 0) {
        const updated = [...prev]
        const existing = prev[idx]
        if (existing.metadata?.type === 'progress' && msg.metadata?.type !== 'progress') {
          updated[idx] = { ...msg, content: msg.content ? existing.content + '\n\n' + msg.content : existing.content }
        } else {
          updated[idx] = msg
        }
        return updated
      }
      return [...prev, msg]
    })
  }, [])

  const clearMessages = useCallback(() => setMessages([]), [])

  return { messages, isThinking, isLoadingHistory, currentStep, sendMessage, appendMessage, clearMessages }
}
